import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { useOutsideClick } from 'src/Hooks';
import { Button, ClearBtn } from 'src/Components';
import { SimpleSelect, EnumsType } from '../Controls';
import { ReactComponent as Shevron } from 'src/Themes/assets/chevron-left.svg';
import MaskInput from '../Controls/MaskInput/MaskInput';
import DateFormater from 'src/Utils/DateFormater';
import clx from './calendar.module.scss';

/** Тип позиции ( top, left ) */
export type PositionType = {
	top: number;
	left: number;
} | null;

interface DatePickerProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
	/** Тип календаря */
	type?: 'once' | 'between' | 'list';
	/** Максимальное количество выбранных дат */
	maxSelectedDate?: number;

	values: Array<Date | null>;
	onSelectDate?: (value: Date | null) => void;
	onChangeValues?: (values: Array<Date | null>) => void;

	disabled?: boolean;

	headless?: boolean;
	onClose?: () => void;

	minDate?: Date;
	maxDate?: Date;

	position?: PositionType;
	onOutSideClick?: (e: any) => void;
}

/** Компонента календаря */
const Calendar: React.FC<DatePickerProps> = (props) => {
	const {
		className,
		style,
		type,
		maxSelectedDate = -1,
		disabled = false,
		values,
		position,
		onSelectDate,
		onChangeValues,
		onOutSideClick,
		headless = true,
		onClose,
		minDate = new Date(OLD_YEAR, 0, 1),
		maxDate = new Date(MAX_YEAR, 11, 31),
	} = props;

	const modalRef = useOutsideClick<HTMLDivElement>((e) => {
		return !!onOutSideClick && onOutSideClick(e);
	}, 'mouseup');

	// Текущая Дата ( ЮТС )
	const [now] = useState<Date>(() => {
		const tmp = new Date(Date.now());
		return new Date(
			tmp.getFullYear(),
			tmp.getMonth(),
			tmp.getDate(),
			0,
			0,
			0,
			0,
		);
	});
	// Месяц
	const [month, setMonth] = useState<number>(now.getMonth());
	// Год
	const [year, setYear] = useState<number>(now.getFullYear());
	// Массив с датами на текущий месяц + граничные ( 42 значения )
	const [calendar, setCalendar] = useState<Date[]>([]);
	// Массив с доступными для выбора годами
	const [yearList, setYearList] = useState<Array<number>>([]);
	// Флаг установки начального значения
	const [changeStart, setChangeStart] = useState<boolean>(true);

	/** При монтировании компонента, вычисляем список доступных годов  */
	useEffect(() => {
		const years = Array(maxDate.getFullYear() - minDate.getFullYear() + 1)
			.fill(0)
			.map((_, index) => {
				return maxDate.getFullYear() - index;
			});
		setYearList(years);
		// eslint-disable-next-line
	}, []);

	/** Получаем текущие значения
	 * и устанавливаем на основе флага установки начального значения,
	 * месяц и год календаря  
	 */
	useEffect(() => {
		setCurrentParams(values[0]);
		// eslint-disable-next-line
	}, [values]);

	/** Генерируем массив с днями  */
	useEffect(() => {
		// День недели первого дня, установленного месяца
		const firstDayThisMonth = new Date(year, month, 1).getDay();
		const temp = [];
		for (let i = 0; i < 42; i++) {
			// Онотоле Отаке !!!
			// Определяем отступ (в днях) для отображения соседних дат
			const day = getShiftDayOfFirstDayThisMonth(firstDayThisMonth);
			temp.push(new Date(year, month, i - day, 0, 0, 0, 0));
		}
		setCalendar(temp);
	}, [month, year]);

	/** Функция вызова одноименной функции из пропсов ( если она назначена ) */
	const onSelectDayHandler = (day: Date | null) => {
		if (onSelectDate) {
			onSelectDate(day);
		}
	};

	/** Функция вызова одноименной функции из пропсов ( если она назначена ) */
	const onChangeValuesHandler = (vals: Array<Date | null>) => {
		if (onChangeValues) {
			onChangeValues(vals);
		}
	};

	/** Устанавливает значение по указанному индексу  */
	const onChangeValueByInput = (val: string, index = 0) => {
		const newValue = DateFormater.parseDate(val, true);
		selectDate(null, newValue);
	};

	/** Возвращает значение по индексу в случае использовании значения в диапазоне */
	const getValue = (index = 0): Date | null => {
		if (type === 'between' && !!values) {
			return values[index] ? values[index] : null;
		} else if (values) {
			return values[0];
		}

		return null;
	};

	/** Устанавливает месяц и год по указанной дате */
	const setCurrentParams = (date: Date | null) => {
		let val = now;
		if (
			!date ||
			typeof date === 'string' ||
			date.toString() === 'Invalid Date'
		) {
			val = now;
			// @ts-ignore
		} else if (date.getDate) {
			val = date;
		}
		setMonth(val.getMonth());
		setYear(val.getFullYear());
	};

	/** Функция устанавливает дату  */
	const selectDate = (e: any, day: Date | null) => {
		if (!!e && e.nativeEvent.pointerType !== 'mouse') {
			return;
		}
		if (day) {
			setMonth(day.getMonth());
			setYear(day.getFullYear());
		}
		onSelectDayHandler(day);
		if (type === 'between') {
			const newDates = [...values];
			if (changeStart) {
				newDates[0] = day;
				setChangeStart(false);
			} else {
				newDates[1] = day;
				setChangeStart(true);
			}
			onChangeValuesHandler(newDates);
		} else if (type === 'once') {
			onChangeValuesHandler([day]);
		} else if (
			type === 'list' &&
			(maxSelectedDate === -1 || values.length < maxSelectedDate)
		) {
			values.push(day);
			onChangeValuesHandler(values);
		}
	};

	/** Слайд месяцев  */
	const changeMonth = (inc: number) => {
		let curMonth = month + inc;
		let curYear = year;

		if (curMonth === 12) {
			curMonth = 0;
			curYear++;
		} else if (curMonth === -1) {
			curMonth = 11;
			curYear--;
		}

		setMonth(curMonth);
		setYear(curYear);
	};

	/** Получение списка классов для дня ( Текущая дата, установленные даты, выходные и дни выходящий из текущего месяца ) */
	const getClassesDay = (day: Date) => {
		const isInner = day.getMonth() === month;
		const curDate = day.getTime();
		const vals = values.map((sd) => {
			return sd?.getTime();
		});
		return [
			clx.date,
			isInner ? '' : clx.outside,
			curDate === now.getTime() ? clx.current : '',
			vals.includes(curDate) ? clx.changed : '',
			isInner && [6, 0].includes(day.getDay()) ? clx.weekend : '',
		].join(' ');
	};

	/** Возвращает массив с именами классов для обвертки над днем ( для подсветки дней находящихся в диапазоне ) */
	const getDayWrapperClasses = (day: Date) => {
		if (type === 'between') {
			const curDate = day.getTime();
			const val0 = values[0]?.getTime();
			const val1 = values[1]?.getTime();
			return [
				!!val0 && !!val1 && curDate > val0 && curDate < val1
					? clx.inBetween
					: '',
			].join(' ');
		}
		return '';
	};

	/** Проверка на доступность дня */
	const isDisabledDay = (day: Date) => {
		const curDate = day.getTime();
		if (curDate < minDate.getTime() || curDate > maxDate.getTime()) {
			return true;
		}
		if (type === 'between') {
			const startValue = values[0]?.getTime() ?? 0;
			const endValue = values[1]?.getTime() ?? 0;

			if (!changeStart) {
				return !!startValue && curDate < startValue;
			} else if (endValue) {
				return endValue < curDate;
			}
		}
		return false;
	};

	/** Отрисовка дня */
	const renderDay = (day: Date, index: number) => {
		return (
			<li key={index} className={getDayWrapperClasses(day)}>
				<button
					type="button"
					className={getClassesDay(day)}
					onClick={(e) => {
						return selectDate(e, day);
					}}
					disabled={isDisabledDay(day) || disabled}
				>
					{day.getDate()}
				</button>
			</li>
		);
	};

	/** Отрисовка тела календаря  */
	const renderBody = () => {
		return (
			<div className={cn(clx.body, 'plain-small')}>
				<ul className={clx.header_days}>
					{DAY_NAMES.map((day, i) => {
						return (
							<li
								className={cn(
									clx.header_day,
									i > 4 ? clx.weekend : '',
								)}
								key={day}
							>
								{day}
							</li>
						);
					})}
				</ul>
				<ul className={clx.days_container}>
					{calendar.map(renderDay)}
				</ul>
			</div>
		);
	};

	/** Рендер контролов  */
	const renderControls = () => {
		return (
			<div className={clx.calendar__controls}>
				<SimpleSelect
					options={MONTH_NAMES}
					value={month}
					setValue={(e) => {
						return setMonth(+e);
					}}
					placeholder="Месяц"
				/>
				<SimpleSelect
					style={{ minWidth: '94px' }}
					options={yearList}
					value={year}
					setValue={(e) => {
						return setYear(+e);
					}}
				/>
				<div className={clx.navbtns}>
					<Button
						outline
						size="min"
						disable={
							minDate.getFullYear() === year &&
							minDate.getMonth() === month
						}
						onClick={() => {
							return changeMonth(-1);
						}}
					>
						<Shevron width={24} />
					</Button>
					<Button
						outline
						size="min"
						disable={
							maxDate.getFullYear() === year &&
							maxDate.getMonth() === month
						}
						onClick={() => {
							return changeMonth(+1);
						}}
					>
						<Shevron width={24} />
					</Button>
				</div>
			</div>
		);
	};

	/** Рендер инпутов  */
	const renderPlaceHolder = () => {
		return (
			<div className={clx.calendar__placeholder}>
				<MaskInput
					disabled={disabled}
					mask="99.99.9999"
					placeholder="ДД.ММ.ГГГГ"
					setValue={onChangeValueByInput}
					onClick={() => {
						return setChangeStart(true);
					}}
					value={DateFormater.toShortString(getValue())}
					className={cn(clx.calendar__placeholderInput, {
						[clx.active]: changeStart,
					})}
				/>
				{type === 'between' && (
					<>
						{'-'}
						<MaskInput
							disabled={disabled}
							mask="99.99.9999"
							placeholder="ДД.ММ.ГГГГ"
							onClick={() => {
								return setChangeStart(false);
							}}
							value={DateFormater.toShortString(getValue(1))}
							setValue={(e: string) => {
								return onChangeValueByInput(e, 1);
							}}
							className={cn(clx.calendar__placeholderInput, {
								[clx.active]: !changeStart,
							})}
						/>
					</>
				)}
			</div>
		);
	};

	/** Рендер шапки  */
	const renderHead = () => {
		if (headless) {
			return null;
		}
		return (
			<div className={clx.calendar__header}>
				<h4>Календарь</h4>
				<ClearBtn isClose notHint onClick={onClose} />
			</div>
		);
	};

	const getHeight = () => {
		let height = 334;
		if (!headless) {
			height += 44;
		}
		if (type === 'between') {
			height += 50;
		}
		return height + 'px';
	};

	return (
		<div
			ref={modalRef}
			className={cn(clx.calendar, className)}
			style={{ ...style, ...position, height: getHeight() }}
		>
			{renderHead()}
			{type === 'between' && renderPlaceHolder()}
			{renderControls()}
			{renderBody()}
		</div>
	);
};

/** Парсит дату из строки  */
const parseDate = (value: any) => {
	if (!value) {
		return null;
	}
	const date = new Date(Date.parse(value));
	date.setHours(0);
	return date;
};

/** Возвращает сдвиг по дням недели для отображения граничных значений */
const getShiftDayOfFirstDayThisMonth = (firstDayThisMonth: number): number => {
	return firstDayThisMonth === 0 ? 5 : firstDayThisMonth - 2;
};

/** Перечисление с месяцами */
const MONTH_NAMES: EnumsType = [
	{
		id: 0,
		title: 'Январь',
	},
	{
		id: 1,
		title: 'Февраль',
	},
	{
		id: 2,
		title: 'Март',
	},
	{
		id: 3,
		title: 'Апрель',
	},
	{
		id: 4,
		title: 'Май',
	},
	{
		id: 5,
		title: 'Июнь',
	},
	{
		id: 6,
		title: 'Июль',
	},
	{
		id: 7,
		title: 'Август',
	},
	{
		id: 8,
		title: 'Сентябрь',
	},
	{
		id: 9,
		title: 'Октябрь',
	},
	{
		id: 10,
		title: 'Ноябрь',
	},
	{
		id: 11,
		title: 'Декабрь',
	},
];
// Минимальный год
const OLD_YEAR = 1940;
// Максимальный год
const MAX_YEAR = new Date().getFullYear() + 5;
// Дни недели
const DAY_NAMES = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

export default Calendar;
